import dayjs from 'utils/dayjs';
import merge from 'lodash/merge';
import EnvironmentUtils from '@spothero/utils/environment';

/** @module hooks/config */

/**
 * Timeformat for the api in 24 hour format
 * @type {string}
 * @example 'HH:mm' => '23:00'
 */
export const apiTimeFormat = 'HH:mm';

/**
 * Dateformat for the api
 * @type {string}
 * @example 'YYYY-MM-DD' => '2020-01-01'
 */
export const apiDateFormat = 'YYYY-MM-DD';

export const apiDateTimeFormat = `${apiDateFormat}T${apiTimeFormat}`;

/**
 *  Timeformat for the display in 12 hour format
 * @type {string}
 * @example 'hh:mm A' => '11:00 PM'
 */
export const displayTimeMeridiem = 'hh:mm A';

/**
 * Date format display as 2 digit month and day and 4 digit year
 * @type {string}
 * @example 'MM/DD/YYYY' => '01/01/2020'
 */
export const displayDateFormat = 'MM/DD/YYYY';

/**
 * Time format display in 24 hour format with seconds
 * @type {string}
 * @example 'HH:mm:ss' => '23:00:00'
 */
export const display24HourFormat = 'HH:mm:ss';

/**
 * Date and Time format display in 12 hour format with seconds
 * @type {string}
 * @example 'ddd, MMM D, YYYY hh:mm:ss A' => 'Mon, Jan 1, 2020 11:00:00 PM'
 */
export const displayDateTimeMeridiem = `ddd, MMM D, YYYY ${displayTimeMeridiem}`;

/**
 * Date format display as Month and Year
 * @type {string}
 * @example 'MMMM YYYY' => 'January 2020'
 */
export const fullMonthYearFormat = 'MMMM YYYY';

/**
 * Config values set during run time
 * @constant
 * @property {string} auth0Domain - Auth0 domain
 * @property {string} auth0ClientId - Auth0 client id
 * @property {string} auth0V1APIAudience - Auth0 audience for the v1 monolith api.
 * @property {string} auth0CPServiceAudience - Auth0 audience for the control panel service.
 * @property {string} basename - the base path for the application. e.g. '/control-panel'
 * @property {string} spotHeroAPIUrl - the base path for the SpotHero API. e.g. '/api/v1'
 * @property {string} cpAPIUrl - the base path for the Control Panel API. e.g. '/control-panel/v1'
 * @property {string} spotheroIQAPIUrl - the base path for the SpotHero IQ API. e.g. 'https://iq.spothero.com/api/v1'
 * @property {string} sentryAPIPublicKey - the public key for Sentry.
 * @property {string} siteUrl - the base url for the SpotHero website. e.g. 'https://spothero.com'
 * @property {string} spotHeroMixpanelToken - the token for SpotHero's Mixpanel account.
 * @property {string} segmentApiKey - the API key for Segment.
 * @property {boolean} isSegmentEnabled - whether or not Segment is enabled.
 * @property {string} wootricAccountToken - the token for SpotHero's Wootric account.
 * @property {boolean} isWootricEnabled - whether or not Wootric is enabled.
 * @property {boolean} forceWootricSurvey - whether or not to force a Wootric survey to appear.
 * @property {boolean} debugSegmentInConsole - whether or not to log Segment events to the console.
 * @property {boolean} debugWootricInConsole - whether or not to log Wootric events to the console.
 * @property {boolean} isTest - whether or not the application is running in a test environment.
 * @property {boolean} isDeployed - whether or not the application is deployed.
 * @property {boolean} isProduction - whether or not the application is running in a production environment.
 * @property {boolean} isMobileDevice - whether or not the application is running on a mobile device.
 * @property {string} displayDateFormat - the format for displaying dates. e.g. 'MM/DD/YYYY'
 * @property {string} display24HourFormat - the format for displaying 24 hour times. e.g. 'HH:mm:ss'
 * @property {string} displayTimeMeridiem - the format for displaying times with meridiem. e.g. 'hh:mm A'
 * @property {string} displayDateTimeMeridiem - the format for displaying date times with meridiem. e.g. 'ddd, MMM D, YYYY hh:mm A'
 * @property {string} apiDateFormat - the format for dates in the API. e.g. 'YYYY-MM-DD'
 * @property {string} apiDateTimeFormat - the format for date times in the API. e.g. 'YYYY-MM-DDTHH:mm'
 * @property {string} apiTimeFormat - the format for times in the API. e.g. 'HH:mm'
 * @property {string} accountingStart - the start date for accounting. e.g. '2017-01-01'
 * @property {string} accountingEnd - the end date for accounting. e.g. '2017-01-01'
 * @property {boolean} wasReservationsReferredFromDashboard - whether or not the user was referred from the dashboard.
 * @property {string} cloudfrontViewerCountryCode - the country code of the user.
 * @property {string} supportEmail - the email address for support.
 * @property {string} helpCenterUrl - Url to the intercom help center
 * @property {function} set - a function to set the properties of the Config object at run time.
 *
 *
 */

const Config = {
    auth0Domain: process.env.VITE_AUTH0_DOMAIN,
    auth0ClientId: process.env.VITE_AUTH0_CLIENT_ID,
    auth0V1APIAudience: process.env.VITE_AUTH0_V1_API_AUDIENCE,
    auth0CPServiceAudience: process.env.VITE_AUTH0_CP_SERVICE_AUDIENCE,
    basename: process.env.VITE_BASE_PATH || '/control-panel',
    authAPIPath: process.env.VITE_AUTH_API_PATH,
    authAPIUrl: process.env.VITE_AUTH_API_URL,
    spotHeroAPIUrl: process.env.VITE_SPOTHERO_API_URL,
    spotHeroAPIPath: process.env.VITE_SPOTHERO_API_PATH,
    spotHeroAPIPathV2: process.env.VITE_SPOTHERO_API_PATH_V2,
    cpAPIUrl: process.env.VITE_CP_API_URL,
    cpAPIPath: process.env.VITE_CP_API_PATH,
    envType: process.env.VITE_ENV_TYPE,
    buildTag: process.env.VITE_SH_BUILD_TAG,
    biServiceAPIUrl: process.env.VITE_BI_SERVICE_API_URL,
    sentryAPIPublicKey: process.env.VITE_SENTRY_API_PUBLIC_KEY,
    siteUrl: process.env.VITE_SITE_URL,
    spotHeroMixpanelToken: process.env.MIXPANEL_TOKEN,
    segmentApiKey: process.env.VITE_SEGMENT_KEY,
    isSegmentEnabled: true,
    wootricAccountToken: process.env.VITE_WOOTRIC_TOKEN,
    isWootricEnabled: false,
    forceWootricSurvey: false,
    debugSegmentInConsole: false,
    debugWootricInConsole: false,
    isTest: process.env.VITE_NODE_ENV === 'test',
    isDeployed: process.env.VITE_IS_DEPLOYED
        ? JSON.parse(process.env.VITE_IS_DEPLOYED)
        : false,
    isProduction: process.env.VITE_IS_PRODUCTION
        ? JSON.parse(process.env.VITE_IS_PRODUCTION)
        : false,
    isMobileDevice: EnvironmentUtils.isMobileDevice(),
    displayDateFormat,
    display24HourFormat,
    displayTimeMeridiem,
    displayDateTimeMeridiem,
    apiDateFormat,
    apiTimeFormat,
    apiDateTimeFormat,
    accountingStart: '2017-01-01',
    accountingEnd: dayjs().format(apiDateFormat),
    wasReservationsReferredFromDashboard: false,
    cloudfrontViewerCountryCode: null,
    supportEmail: 'sellersupport@spothero.com',
    spotHeroIQURL: 'https://spothero.com/sell-parking/iq',
    helpCenterUrl: 'https://operator-help.spothero.com/',
    googleMapsApiKey: process.env.VITE_GOOGLE_MAP_API_KEY,
    intercomAppId: process.env.VITE_INTERCOM_APP_ID,

    set(opts) {
        merge(this, opts);
    },
};

export default Config;
